import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CalendarDays, MapPin, Clock, Search, Loader2 } from "lucide-react";

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const { t, tr } = useLang();

  useEffect(() => {
    api.get("/public/events").then((r) => setEvents(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = events.filter((e) =>
    [e.name, e.location, e.description].join(" ").toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div data-testid="public-events-page">
      <section className="bg-[#0E2916] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C59B27] font-mono">{t("Trust Calendar")}</p>
          <h1 className="font-display text-4xl lg:text-5xl font-semibold mt-2">{t("Events")}</h1>
          <p className="text-white/70 mt-3 max-w-xl">{t("Camps, satsangs, welfare drives and spiritual gatherings organized by the trust.")}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <div className="relative max-w-md mb-10">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA99F]" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("Search events...")} className="pl-10 rounded-full" data-testid="events-search-input" />
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#C59B27]" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-[#5C7264]"><CalendarDays className="h-12 w-12 mx-auto mb-4 text-[#CBD2CC]" /><p>{t("No events found.")}</p></div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((e) => (
              <button key={e.id} onClick={() => setSelected(e)} className="text-left bg-white rounded-xl border border-[#E2DAD0] overflow-hidden hover:-translate-y-1 hover:shadow-md hover:border-[#C59B27] transition-all" data-testid={`public-event-${e.id}`}>
                <div className="h-48 bg-[#E8F0EA]">{e.image_path ? <img src={fileUrl(e.image_path)} alt={e.name} className="h-full w-full object-cover" /> : <div className="h-full flex items-center justify-center"><CalendarDays className="h-10 w-10 text-[#C59B27]/40" /></div>}</div>
                <div className="p-5">
                  <div className="inline-flex items-center gap-2 text-xs font-mono text-[#B38728] bg-[#F9F3E3] rounded-full px-3 py-1"><CalendarDays className="h-3 w-3" />{e.date}{e.time && ` · ${e.time}`}</div>
                  <h3 className="font-display text-xl font-semibold text-[#194324] mt-3">{tr(e, "name")}</h3>
                  {e.location && <p className="text-sm text-[#5C7264] mt-1 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{e.location}</p>}
                  <p className="text-sm text-[#3A4E41] mt-2 line-clamp-2">{tr(e, "description")}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl text-[#194324]">{selected.name}</DialogTitle></DialogHeader>
              {selected.image_path && <img src={fileUrl(selected.image_path)} alt={selected.name} className="rounded-lg w-full h-56 object-cover" />}
              <div className="flex flex-wrap gap-4 text-sm text-[#5C7264] mt-2">
                <span className="flex items-center gap-1"><CalendarDays className="h-4 w-4 text-[#C59B27]" />{selected.date}</span>
                {selected.time && <span className="flex items-center gap-1"><Clock className="h-4 w-4 text-[#C59B27]" />{selected.time}</span>}
                {selected.location && <span className="flex items-center gap-1"><MapPin className="h-4 w-4 text-[#C59B27]" />{selected.location}</span>}
              </div>
              <p className="text-[#3A4E41] leading-relaxed mt-2 whitespace-pre-line">{selected.description}</p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
